import React from 'react';
import { CheckCircle, AlertCircle, Lightbulb, RotateCcw, XCircle, RefreshCw } from 'lucide-react';

export default function ResultsDisplay({ results, onReset, onEditAnswers }) {
  // Handle no results or empty results
  if (!results || !results.universities || results.universities.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
            <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Recommendations Generated</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              The AI didn't generate any recommendations. This might be because the analysis is incomplete or there was an issue processing your responses.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onReset}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 justify-center"
              >
                <RefreshCw className="w-5 h-5" />
                Start Over
              </button>
              <button
                onClick={onEditAnswers}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium flex items-center gap-2 justify-center"
              >
                ✏️ Try Editing Answers
              </button>
            </div>

            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>Tip:</strong> Make sure you complete all required questions and provide detailed answers for better recommendations.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-blue-600 bg-blue-50';
    return 'text-amber-600 bg-amber-50';
  };

  const getScoreBadge = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-700';
    if (score >= 60) return 'bg-blue-100 text-blue-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 pt-32 pb-12 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3 headline-serif">Your University Recommendations</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">Based on your profile, preferences, and portfolio</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center mb-8">
          <button
            onClick={onEditAnswers}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
          >
            ✏️ Edit Answers
          </button>
          <button
            onClick={onReset}
            className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
        </div>
      </div>

      {/* Universities Grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
        {results.universities.map((uni, idx) => (
          <div
            key={idx}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-t-4 border-blue-500"
          >
            {/* Header with Score */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-6 text-white">
              <h2 className="text-2xl font-bold mb-2">{uni.name}</h2>
              <p className="text-blue-100 mb-4">{uni.country}</p>
              <div className={`inline-block px-4 py-2 rounded-full ${getScoreBadge(uni.matching_score)}`}>
                <span className="font-bold text-lg">{uni.matching_score}%</span> Match
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Met Requirements */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">You Meet</h3>
                </div>
                <ul className="space-y-2">
                  {uni.eligibility.met_requirements.map((req, reqIdx) => (
                    <li key={reqIdx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-green-600 font-bold mt-1">✓</span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Missing Requirements */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">Work On</h3>
                </div>
                <ul className="space-y-2">
                  {uni.eligibility.missing_requirements.map((req, reqIdx) => (
                    <li key={reqIdx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-amber-600 font-bold mt-1">◆</span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Suggestions */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Tips to Strengthen Your Application</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{uni.eligibility.suggestions}</p>
                  </div>
                </div>
              </div>

              {/* View Details Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => results.onViewDetails && results.onViewDetails(uni)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Info */}
      <div className="max-w-6xl mx-auto mt-12 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md text-center">
        <p className="text-gray-600 dark:text-gray-300">
          💡 <span className="font-semibold">Next Steps:</span> Research these universities, prepare your application materials, and
          use the tips above to strengthen your profile!
        </p>
      </div>
    </div>
  );
}
